import { formatCurrency } from "@/lib/currency";
/**
 * Operations Command Centre — data + intelligence layer.
 *
 * Additive service. Reads only existing tables/columns and derives every
 * operational view (KPIs, board lanes, attention items, timeline, driver and
 * vehicle status, customer alerts) on the client. No schema, route,
 * permission or business-logic changes.
 */
import { supabase } from "@/integrations/supabase/client";

export type OpsLane =
  | "scheduled"
  | "assigned"
  | "collected"
  | "in_transit"
  | "arriving"
  | "delivered"
  | "completed";

export const OPS_LANES: { id: OpsLane; label: string }[] = [
  { id: "scheduled", label: "Scheduled" },
  { id: "assigned", label: "Assigned" },
  { id: "collected", label: "Collected" },
  { id: "in_transit", label: "In Transit" },
  { id: "arriving", label: "Arriving Soon" },
  { id: "delivered", label: "Delivered" },
  { id: "completed", label: "Completed" },
];

export type OpsSeverity = "critical" | "high" | "medium" | "low";
export type OpsPriority = "high" | "normal";

export interface OpsJob {
  id: string;
  reference: string;
  status: string;
  lane: OpsLane;
  priority: OpsPriority;
  vehicle: string;
  registration: string;
  customerId: string | null;
  customerName: string;
  customerStatus: string | null;
  customerPhone: string | null;
  driverId: string | null;
  driverName: string | null;
  driverPhone: string | null;
  collection: string;
  collectionPostcode: string | null;
  delivery: string;
  deliveryPostcode: string | null;
  scheduledAt: string | null;
  createdAt: string;
  collectedAt: string | null;
  deliveredAt: string | null;
  completedAt: string | null;
  minutesLate: number;
  invoiceGenerated: boolean;
  invoiceNumber: string | null;
  paymentStatus: string | null;
  amountDue: number;
  trackingShared: boolean;
  collectionAppraisalId: string | null;
  deliveryAppraisalId: string | null;
  accountingSynced: boolean;
  isToday: boolean;
}

export interface OpsDriver {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  avatarUrl: string | null;
  status: string;
  isActive: boolean;
  latitude: number | null;
  longitude: number | null;
  lastLocationUpdate: string | null;
  currentJob: OpsJob | null;
  jobsToday: number;
  availability: "working" | "available" | "offline";
  /** Not tracked yet — surfaced as future-ready in the UI. */
  hoursWorked: number | null;
}

export interface OpsVehicle {
  id: string;
  registration: string;
  label: string;
  driverName: string | null;
  jobReference: string | null;
  jobId: string | null;
  checkedToday: boolean;
  hasDefects: boolean;
  defectNotes: string | null;
  warnings: string[];
  active: boolean;
  /** Not tracked yet — future ready. */
  fuelAlert: string | null;
  capacity: string | null;
}

export interface OpsAttentionItem {
  id: string;
  severity: OpsSeverity;
  title: string;
  detail: string;
  jobId?: string;
  category: string;
}

export interface OpsEvent {
  id: string;
  at: string;
  title: string;
  detail: string;
  jobId?: string;
  type: "job" | "appraisal" | "invoice" | "message" | "check" | "tracking";
}

export interface OpsCustomerAlert {
  id: string;
  customerId: string | null;
  name: string;
  reason: string;
  detail: string;
  severity: OpsSeverity;
  jobId?: string;
}

export interface OpsMessage {
  id: string;
  driverId: string;
  driverName: string;
  title: string;
  body: string;
  createdAt: string;
}

export interface OperationsSnapshot {
  jobs: OpsJob[];
  drivers: OpsDriver[];
  vehicles: OpsVehicle[];
  messages: OpsMessage[];
  attention: OpsAttentionItem[];
  timeline: OpsEvent[];
  customerAlerts: OpsCustomerAlert[];
  kpis: OpsKpi[];
  generatedAt: string;
}

export interface OpsKpi {
  id: string;
  label: string;
  value: number;
  tone: "default" | "success" | "warning" | "danger";
  /** Filter applied when the KPI is clicked. */
  filter: OpsKpiFilter;
}

export type OpsKpiFilter =
  | { kind: "lane"; lane: OpsLane }
  | { kind: "jobs"; ids: string[]; label: string }
  | { kind: "panel"; panel: "drivers" | "vehicles" | "messages" | "attention" };

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const num = (value: unknown) => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const minutesSince = (iso: string | null) =>
  iso ? Math.round((Date.now() - new Date(iso).getTime()) / 60000) : Number.POSITIVE_INFINITY;

const JOB_COLUMNS = `
  id, job_reference, status, vehicle_make, vehicle_model, vehicle_registration,
  customer_id, driver_id, collection_address, collection_postcode, delivery_address,
  delivery_postcode, scheduled_date, scheduled_time, created_at, completed_at,
  collection_completed_at, delivery_completed_at, invoice_generated, invoice_number,
  payment_status, amount_due, invoice_amount, tracking_shared, collection_appraisal_id,
  delivery_appraisal_id, quickbooks_synced_at, xero_synced_at, freeagent_synced_at,
  customers:customer_id ( id, name, phone, customer_status ),
  drivers:driver_id ( id, name, phone )
`;

function toOpsJob(row: Record<string, any>, driverLocations: Map<string, string | null>): OpsJob {
  const customer = row.customers ?? null;
  const driver = row.drivers ?? null;
  const scheduledAt: string | null = row.scheduled_date ?? null;
  const today = startOfToday().getTime();
  const isToday = [row.scheduled_date, row.created_at, row.completed_at]
    .filter(Boolean)
    .some((v: string) => new Date(v).getTime() >= today);

  const dueMinutes = scheduledAt ? Math.round((Date.now() - new Date(scheduledAt).getTime()) / 60000) : 0;
  const finished = row.status === "completed" || !!row.completed_at;
  const minutesLate = finished ? 0 : Math.max(0, dueMinutes);

  let lane: OpsLane = "scheduled";
  if (row.status === "completed") lane = "completed";
  else if (row.delivery_completed_at) lane = "delivered";
  else if (row.collection_completed_at) {
    const lastPing = row.driver_id ? driverLocations.get(row.driver_id) ?? null : null;
    if (lastPing && minutesSince(lastPing) <= 10) lane = "arriving";
    else if (row.status === "awaiting_delivery") lane = "in_transit";
    else lane = "collected";
  } else if (row.driver_id) lane = "assigned";

  const vip = (customer?.customer_status ?? "").toLowerCase() === "vip";
  const priority: OpsPriority = vip || minutesLate > 30 ? "high" : "normal";

  return {
    id: row.id,
    reference: row.job_reference || row.vehicle_registration || row.id.slice(0, 8),
    status: row.status,
    lane,
    priority,
    vehicle: `${row.vehicle_make ?? ""} ${row.vehicle_model ?? ""}`.trim(),
    registration: row.vehicle_registration ?? "",
    customerId: customer?.id ?? row.customer_id ?? null,
    customerName: customer?.name ?? "Unknown customer",
    customerStatus: customer?.customer_status ?? null,
    customerPhone: customer?.phone ?? null,
    driverId: row.driver_id ?? null,
    driverName: driver?.name ?? null,
    driverPhone: driver?.phone ?? null,
    collection: row.collection_address ?? "",
    collectionPostcode: row.collection_postcode ?? null,
    delivery: row.delivery_address ?? "",
    deliveryPostcode: row.delivery_postcode ?? null,
    scheduledAt,
    createdAt: row.created_at,
    collectedAt: row.collection_completed_at ?? null,
    deliveredAt: row.delivery_completed_at ?? null,
    completedAt: row.completed_at ?? null,
    minutesLate,
    invoiceGenerated: !!row.invoice_generated,
    invoiceNumber: row.invoice_number ?? null,
    paymentStatus: row.payment_status ?? null,
    amountDue: num(row.amount_due ?? row.invoice_amount),
    trackingShared: !!row.tracking_shared,
    collectionAppraisalId: row.collection_appraisal_id ?? null,
    deliveryAppraisalId: row.delivery_appraisal_id ?? null,
    accountingSynced: !!(row.quickbooks_synced_at || row.xero_synced_at || row.freeagent_synced_at),
    isToday,
  };
}

export async function loadOperationsSnapshot(companyId: string): Promise<OperationsSnapshot> {
  const todayIso = startOfToday().toISOString();
  const weekAgoIso = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString();
  const todayDate = todayIso.slice(0, 10);

  const [activeJobsRes, recentJobsRes, driversRes, vehiclesRes, checksRes, messagesRes, appraisalsRes] =
    await Promise.all([
      supabase
        .from("jobs")
        .select(JOB_COLUMNS)
        .eq("company_id", companyId)
        .is("archived_at", null)
        .not("status", "in", "(completed,cancelled)")
        .order("scheduled_date", { ascending: true })
        .limit(500),
      supabase
        .from("jobs")
        .select(JOB_COLUMNS)
        .eq("company_id", companyId)
        .is("archived_at", null)
        .gte("updated_at", todayIso)
        .order("updated_at", { ascending: false })
        .limit(300),
      supabase.from("drivers").select("*").eq("company_id", companyId).limit(500),
      supabase.from("vehicles").select("*").eq("company_id", companyId).limit(500),
      supabase
        .from("vehicle_daily_checks")
        .select("id, vehicle_id, vehicle_registration, driver_id, check_date, has_defects, defect_notes, overall_status, created_at")
        .eq("company_id", companyId)
        .eq("check_date", todayDate)
        .limit(500),
      supabase
        .from("driver_messages")
        .select("id, driver_id, title, body, created_at, read_at")
        .eq("company_id", companyId)
        .is("read_at", null)
        .gte("created_at", weekAgoIso)
        .order("created_at", { ascending: false })
        .limit(200),
      supabase
        .from("vehicle_appraisals")
        .select("id, job_id, appraisal_type, completed_at, created_at")
        .eq("company_id", companyId)
        .gte("created_at", todayIso)
        .limit(300),
    ]);

  const driverRows = (driversRes.data ?? []) as Record<string, any>[];
  const driverLocations = new Map<string, string | null>(
    driverRows.map((d) => [d.id as string, (d.last_location_update as string) ?? null]),
  );

  const rawJobs = [...((activeJobsRes.data ?? []) as any[]), ...((recentJobsRes.data ?? []) as any[])];
  const seen = new Set<string>();
  const jobs: OpsJob[] = [];
  for (const row of rawJobs) {
    if (!row?.id || seen.has(row.id)) continue;
    seen.add(row.id);
    if (row.status === "cancelled") continue;
    jobs.push(toOpsJob(row, driverLocations));
  }

  const jobsByDriver = new Map<string, OpsJob[]>();
  jobs.forEach((job) => {
    if (!job.driverId) return;
    const list = jobsByDriver.get(job.driverId) ?? [];
    list.push(job);
    jobsByDriver.set(job.driverId, list);
  });

  const driverNames = new Map(driverRows.map((d) => [d.id as string, (d.name as string) ?? "Driver"]));

  const drivers: OpsDriver[] = driverRows.map((row) => {
    const list = jobsByDriver.get(row.id) ?? [];
    const current = list.find((j) => j.lane !== "completed" && j.lane !== "scheduled") ?? null;
    const recentPing = minutesSince(row.last_location_update ?? null) <= 30;
    const availability: OpsDriver["availability"] = current
      ? "working"
      : row.is_active || recentPing
        ? "available"
        : "offline";
    return {
      id: row.id,
      name: row.name ?? "Driver",
      phone: row.phone ?? null,
      email: row.email ?? null,
      avatarUrl: row.avatar_url ?? null,
      status: row.status ?? "unknown",
      isActive: !!row.is_active,
      latitude: row.latitude != null ? Number(row.latitude) : null,
      longitude: row.longitude != null ? Number(row.longitude) : null,
      lastLocationUpdate: row.last_location_update ?? null,
      currentJob: current,
      jobsToday: list.filter((j) => j.isToday).length,
      availability,
      hoursWorked: null,
    };
  });

  const checks = (checksRes.data ?? []) as Record<string, any>[];
  const checkByReg = new Map(checks.map((c) => [String(c.vehicle_registration ?? "").toUpperCase(), c]));
  const checkById = new Map(checks.filter((c) => c.vehicle_id).map((c) => [c.vehicle_id as string, c]));

  const vehicles: OpsVehicle[] = ((vehiclesRes.data ?? []) as Record<string, any>[]).map((row) => {
    const check = checkById.get(row.id) ?? checkByReg.get(String(row.registration ?? "").toUpperCase()) ?? null;
    const warnings: string[] = [];
    const soon = (date: string | null, label: string) => {
      if (!date) return;
      const days = Math.round((new Date(date).getTime() - Date.now()) / 86400000);
      if (days < 0) warnings.push(`${label} expired`);
      else if (days <= 30) warnings.push(`${label} due in ${days}d`);
    };
    soon(row.mot_expiry_date ?? null, "MOT");
    soon(row.insurance_expiry_date ?? null, "Insurance");
    soon(row.tax_expiry_date ?? null, "Tax");
    soon(row.service_due_date ?? null, "Service");
    if (check?.has_defects) warnings.push("Defects reported today");

    const driverName = check?.driver_id ? driverNames.get(check.driver_id) ?? null : null;
    const linkedDriver = drivers.find((d) => d.name === driverName && d.currentJob);
    return {
      id: row.id,
      registration: row.registration ?? "",
      label: `${row.make ?? ""} ${row.model ?? ""}`.trim() || (row.registration ?? "Vehicle"),
      driverName,
      jobReference: linkedDriver?.currentJob?.reference ?? null,
      jobId: linkedDriver?.currentJob?.id ?? null,
      checkedToday: !!check,
      hasDefects: !!check?.has_defects,
      defectNotes: check?.defect_notes ?? null,
      warnings,
      active: !!check,
      fuelAlert: null,
      capacity: null,
    };
  });

  const messages: OpsMessage[] = ((messagesRes.data ?? []) as Record<string, any>[]).map((row) => ({
    id: row.id,
    driverId: row.driver_id,
    driverName: driverNames.get(row.driver_id) ?? "Driver",
    title: row.title ?? "Message",
    body: row.body ?? "",
    createdAt: row.created_at,
  }));

  const appraisals = (appraisalsRes.data ?? []) as Record<string, any>[];

  const attention = buildAttention(jobs, drivers, vehicles, messages);
  const timeline = buildTimeline(jobs, appraisals, checks, messages);
  const customerAlerts = buildCustomerAlerts(jobs, messages);
  const kpis = buildKpis(jobs, drivers, vehicles, messages, attention);

  return {
    jobs,
    drivers,
    vehicles,
    messages,
    attention,
    timeline,
    customerAlerts,
    kpis,
    generatedAt: new Date().toISOString(),
  };
}

export function outstandingAppraisalJobs(jobs: OpsJob[]): OpsJob[] {
  return jobs.filter(
    (j) =>
      (j.collectedAt && !j.collectionAppraisalId) ||
      (j.deliveredAt && !j.deliveryAppraisalId),
  );
}

export function outstandingInvoiceJobs(jobs: OpsJob[]): OpsJob[] {
  return jobs.filter(
    (j) =>
      (j.completedAt && !j.invoiceGenerated) ||
      (j.invoiceGenerated && j.paymentStatus !== "paid" && j.amountDue > 0),
  );
}

function buildKpis(
  jobs: OpsJob[],
  drivers: OpsDriver[],
  vehicles: OpsVehicle[],
  messages: OpsMessage[],
  attention: OpsAttentionItem[],
): OpsKpi[] {
  const today = jobs.filter((j) => j.isToday);
  const inProgress = jobs.filter((j) => ["collected", "in_transit", "arriving"].includes(j.lane));
  const waiting = jobs.filter((j) => ["scheduled", "assigned"].includes(j.lane));
  const delayed = jobs.filter((j) => j.minutesLate > 30 && j.lane !== "completed");
  const highPriority = jobs.filter((j) => j.priority === "high" && j.lane !== "completed");
  const appraisals = outstandingAppraisalJobs(jobs);
  const invoices = outstandingInvoiceJobs(jobs);

  const ids = (list: OpsJob[]) => list.map((j) => j.id);

  return [
    { id: "jobs-today", label: "Jobs Today", value: today.length, tone: "default", filter: { kind: "jobs", ids: ids(today), label: "Jobs today" } },
    { id: "in-progress", label: "In Progress", value: inProgress.length, tone: "default", filter: { kind: "jobs", ids: ids(inProgress), label: "Jobs in progress" } },
    { id: "waiting", label: "Waiting Collection", value: waiting.length, tone: "warning", filter: { kind: "jobs", ids: ids(waiting), label: "Waiting collection" } },
    { id: "delayed", label: "Delayed", value: delayed.length, tone: delayed.length ? "danger" : "default", filter: { kind: "jobs", ids: ids(delayed), label: "Delayed jobs" } },
    { id: "drivers-working", label: "Drivers Working", value: drivers.filter((d) => d.availability === "working").length, tone: "success", filter: { kind: "panel", panel: "drivers" } },
    { id: "drivers-available", label: "Drivers Available", value: drivers.filter((d) => d.availability === "available").length, tone: "default", filter: { kind: "panel", panel: "drivers" } },
    { id: "vehicles-active", label: "Vehicles Active", value: vehicles.filter((v) => v.active).length, tone: "success", filter: { kind: "panel", panel: "vehicles" } },
    { id: "vehicles-offline", label: "Vehicles Offline", value: vehicles.filter((v) => !v.active).length, tone: "default", filter: { kind: "panel", panel: "vehicles" } },
    { id: "messages", label: "Messages Waiting", value: messages.length, tone: messages.length ? "warning" : "default", filter: { kind: "panel", panel: "messages" } },
    { id: "appraisals", label: "Outstanding Appraisals", value: appraisals.length, tone: appraisals.length ? "warning" : "default", filter: { kind: "jobs", ids: ids(appraisals), label: "Outstanding appraisals" } },
    { id: "invoices", label: "Outstanding Invoices", value: invoices.length, tone: invoices.length ? "warning" : "default", filter: { kind: "jobs", ids: ids(invoices), label: "Outstanding invoices" } },
    { id: "priority", label: "High Priority", value: highPriority.length, tone: highPriority.length ? "danger" : "default", filter: { kind: "jobs", ids: ids(highPriority), label: "High priority jobs" } },
  ];
}

function buildAttention(
  jobs: OpsJob[],
  drivers: OpsDriver[],
  vehicles: OpsVehicle[],
  messages: OpsMessage[],
): OpsAttentionItem[] {
  const items: OpsAttentionItem[] = [];

  jobs.forEach((job) => {
    if (job.lane !== "completed" && job.minutesLate > 120) {
      items.push({
        id: `late-${job.id}`,
        severity: "critical",
        title: `${job.reference} is ${Math.round(job.minutesLate / 60)}h behind schedule`,
        detail: `${job.customerName} · ${job.registration}`,
        jobId: job.id,
        category: "Late ETA",
      });
    } else if (job.lane !== "completed" && job.minutesLate > 30) {
      items.push({
        id: `late-${job.id}`,
        severity: "high",
        title: `${job.reference} is running late`,
        detail: `${job.minutesLate} minutes past schedule`,
        jobId: job.id,
        category: "Late ETA",
      });
    }

    if (job.lane === "in_transit" || job.lane === "collected") {
      const driver = drivers.find((d) => d.id === job.driverId);
      const idle = driver ? minutesSince(driver.lastLocationUpdate) : Number.POSITIVE_INFINITY;
      if (driver && idle > 45) {
        items.push({
          id: `stopped-${job.id}`,
          severity: "high",
          title: `${driver.name} has not reported movement`,
          detail: `No location update for ${Number.isFinite(idle) ? `${idle} minutes` : "this job"} on ${job.reference}`,
          jobId: job.id,
          category: "Driver stopped",
        });
      }
    }

    if (job.deliveredAt && !job.deliveryAppraisalId) {
      items.push({
        id: `pod-${job.id}`,
        severity: "high",
        title: `Missing PoD on ${job.reference}`,
        detail: "Vehicle delivered without a completed delivery appraisal",
        jobId: job.id,
        category: "Missing PoD",
      });
    }
    if (job.collectedAt && !job.collectionAppraisalId) {
      items.push({
        id: `poc-${job.id}`,
        severity: "medium",
        title: `Inspection outstanding on ${job.reference}`,
        detail: "Collected without a completed collection appraisal",
        jobId: job.id,
        category: "Inspection outstanding",
      });
    }
    if (job.completedAt && !job.invoiceGenerated) {
      items.push({
        id: `invoice-${job.id}`,
        severity: "medium",
        title: `Invoice not generated for ${job.reference}`,
        detail: `${job.customerName} · completed job awaiting invoicing`,
        jobId: job.id,
        category: "Invoice not generated",
      });
    }
    if (job.invoiceGenerated && !job.accountingSynced && job.completedAt) {
      items.push({
        id: `sync-${job.id}`,
        severity: "low",
        title: `Accounting sync pending for ${job.reference}`,
        detail: "Invoice generated but not yet confirmed in the accounting provider",
        jobId: job.id,
        category: "Accounting sync",
      });
    }
    if (job.priority === "high" && job.customerStatus?.toLowerCase() === "vip" && job.lane !== "completed") {
      items.push({
        id: `vip-${job.id}`,
        severity: "medium",
        title: `${job.customerName} is a priority customer`,
        detail: `${job.reference} is active — keep the customer updated`,
        jobId: job.id,
        category: "High priority customer",
      });
    }
  });

  vehicles
    .filter((v) => v.hasDefects || v.warnings.some((w) => w.includes("expired")))
    .forEach((v) =>
      items.push({
        id: `vehicle-${v.id}`,
        severity: v.hasDefects ? "high" : "medium",
        title: `${v.registration || v.label} requires attention`,
        detail: v.hasDefects ? v.defectNotes || "Defects reported on today's check" : v.warnings.join(", "),
        category: "Vehicle overdue",
      }),
    );

  if (messages.length) {
    items.push({
      id: "messages-waiting",
      severity: messages.length > 4 ? "high" : "low",
      title: `${messages.length} driver message${messages.length === 1 ? "" : "s"} unread`,
      detail: "Messages awaiting a reply from the office",
      category: "Customer waiting",
    });
  }

  const order: Record<OpsSeverity, number> = { critical: 0, high: 1, medium: 2, low: 3 };
  return items.sort((a, b) => order[a.severity] - order[b.severity]);
}

function buildTimeline(
  jobs: OpsJob[],
  appraisals: Record<string, any>[],
  checks: Record<string, any>[],
  messages: OpsMessage[],
): OpsEvent[] {
  const since = startOfToday().getTime();
  const events: OpsEvent[] = [];
  const push = (event: OpsEvent) => {
    if (event.at && new Date(event.at).getTime() >= since) events.push(event);
  };

  const jobByRef = new Map(jobs.map((j) => [j.id, j]));

  jobs.forEach((job) => {
    push({ id: `created-${job.id}`, at: job.createdAt, title: "Job created", detail: `${job.reference} · ${job.customerName}`, jobId: job.id, type: "job" });
    if (job.collectedAt) push({ id: `collected-${job.id}`, at: job.collectedAt, title: "Vehicle collected", detail: `${job.reference} · ${job.registration}`, jobId: job.id, type: "job" });
    if (job.deliveredAt) push({ id: `delivered-${job.id}`, at: job.deliveredAt, title: "Vehicle delivered", detail: `${job.reference} · ${job.customerName}`, jobId: job.id, type: "job" });
    if (job.completedAt) push({ id: `completed-${job.id}`, at: job.completedAt, title: "Job completed", detail: job.reference, jobId: job.id, type: "job" });
  });

  appraisals.forEach((a) => {
    const job = jobByRef.get(a.job_id);
    push({
      id: `appraisal-${a.id}`,
      at: a.completed_at ?? a.created_at,
      title: `${a.appraisal_type === "delivery" ? "Delivery" : a.appraisal_type === "interior" ? "Interior" : "Collection"} appraisal uploaded`,
      detail: job ? `${job.reference} · ${job.registration}` : "Appraisal submitted",
      jobId: a.job_id,
      type: "appraisal",
    });
  });

  checks.forEach((c) =>
    push({
      id: `check-${c.id}`,
      at: c.created_at,
      title: "Daily vehicle check completed",
      detail: `${c.vehicle_registration ?? "Vehicle"}${c.has_defects ? " · defects reported" : ""}`,
      type: "check",
    }),
  );

  messages.forEach((m) =>
    push({ id: `message-${m.id}`, at: m.createdAt, title: "Driver message", detail: `${m.driverName} · ${m.title}`, type: "message" }),
  );

  return events.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()).slice(0, 80);
}

function buildCustomerAlerts(jobs: OpsJob[], messages: OpsMessage[]): OpsCustomerAlert[] {
  const alerts: OpsCustomerAlert[] = [];
  jobs.forEach((job) => {
    if (["scheduled", "assigned"].includes(job.lane) && job.minutesLate > 15) {
      alerts.push({
        id: `wait-collection-${job.id}`,
        customerId: job.customerId,
        name: job.customerName,
        reason: "Waiting for collection",
        detail: `${job.reference} scheduled ${job.minutesLate} minutes ago`,
        severity: job.minutesLate > 90 ? "high" : "medium",
        jobId: job.id,
      });
    }
    if (["collected", "in_transit", "arriving"].includes(job.lane)) {
      alerts.push({
        id: `wait-delivery-${job.id}`,
        customerId: job.customerId,
        name: job.customerName,
        reason: "Waiting for delivery",
        detail: `${job.reference} · ${job.trackingShared ? "tracking shared" : "tracking not shared"}`,
        severity: job.trackingShared ? "low" : "medium",
        jobId: job.id,
      });
    }
    if (job.customerStatus?.toLowerCase() === "vip" && job.lane !== "completed") {
      alerts.push({
        id: `vip-alert-${job.id}`,
        customerId: job.customerId,
        name: job.customerName,
        reason: "VIP customer",
        detail: `${job.reference} active`,
        severity: "medium",
        jobId: job.id,
      });
    }
  });
  if (messages.length) {
    alerts.push({
      id: "messages-alert",
      customerId: null,
      name: "Driver messages",
      reason: "Messages awaiting reply",
      detail: `${messages.length} unread`,
      severity: "medium",
    });
  }
  return alerts;
}

/* ------------------------------------------------------------------ */
/* Operations intelligence                                             */
/* ------------------------------------------------------------------ */

export interface OpsInsight {
  id: string;
  tone: "info" | "success" | "warning" | "danger";
  title: string;
  detail: string;
  recommendation?: string;
}

export interface OperationsIntelligenceProvider {
  name: string;
  analyse(snapshot: OperationsSnapshot): OpsInsight[] | Promise<OpsInsight[]>;
}

const region = (postcode: string | null) =>
  (postcode ?? "").trim().toUpperCase().match(/^[A-Z]{1,2}/)?.[0] ?? null;

export const RuleBasedOperationsIntelligenceProvider: OperationsIntelligenceProvider = {
  name: "rules-v1",
  analyse(snapshot) {
    const insights: OpsInsight[] = [];
    const active = snapshot.jobs.filter((j) => j.lane !== "completed");

    const atRisk = active.filter((j) => j.minutesLate > 20);
    if (atRisk.length) {
      insights.push({
        id: "sla-risk",
        tone: atRisk.length > 2 ? "danger" : "warning",
        title: `${atRisk.length} job${atRisk.length === 1 ? "" : "s"} likely to miss SLA`,
        detail: atRisk.slice(0, 4).map((j) => `${j.reference} (${j.minutesLate}m late)`).join(", "),
        recommendation: "Contact the assigned drivers and update the customers before they chase.",
      });
    }

    const unassigned = active.filter((j) => !j.driverId);
    const available = snapshot.drivers.filter((d) => d.availability === "available");
    if (unassigned.length && available.length) {
      insights.push({
        id: "assign-capacity",
        tone: "info",
        title: `${unassigned.length} unassigned job${unassigned.length === 1 ? "" : "s"} with ${available.length} available driver${available.length === 1 ? "" : "s"}`,
        detail: `Available: ${available.slice(0, 4).map((d) => d.name).join(", ")}`,
        recommendation: `Assign ${unassigned[0].reference} to ${available[0].name}.`,
      });
    } else if (unassigned.length && !available.length) {
      insights.push({
        id: "no-capacity",
        tone: "warning",
        title: `${unassigned.length} job${unassigned.length === 1 ? "" : "s"} unassigned with no free drivers`,
        detail: "All drivers are currently on a job.",
        recommendation: "Consider a subcontractor or reschedule the lowest priority job.",
      });
    }

    const loadByRegion = new Map<string, number>();
    active.forEach((job) => {
      const key = region(job.collectionPostcode) ?? region(job.deliveryPostcode);
      if (!key) return;
      loadByRegion.set(key, (loadByRegion.get(key) ?? 0) + 1);
    });
    const ranked = [...loadByRegion.entries()].sort((a, b) => b[1] - a[1]);
    if (ranked.length && ranked[0][1] >= 3) {
      insights.push({
        id: "region-load",
        tone: "warning",
        title: `${ranked[0][0]} region is carrying the heaviest load`,
        detail: `${ranked[0][1]} active jobs in ${ranked[0][0]}${ranked.length > 1 ? `, ${ranked[ranked.length - 1][0]} has spare capacity (${ranked[ranked.length - 1][1]})` : ""}.`,
        recommendation: "Rebalance the next assignments towards the quieter region.",
      });
    }

    const finishingEarly = snapshot.drivers.filter((d) => d.availability === "working" && d.currentJob?.lane === "arriving");
    if (finishingEarly.length >= 2) {
      insights.push({
        id: "finishing-soon",
        tone: "success",
        title: `${finishingEarly.length} drivers will free up shortly`,
        detail: finishingEarly.map((d) => d.name).join(", "),
        recommendation: "Queue the next collections for these drivers now.",
      });
    }

    const invoices = outstandingInvoiceJobs(snapshot.jobs);
    if (invoices.length >= 3) {
      insights.push({
        id: "invoice-backlog",
        tone: "warning",
        title: `${invoices.length} jobs awaiting invoicing or payment`,
        detail: `${formatCurrency(Math.round(invoices.reduce((t, j) => t + j.amountDue, 0)).toLocaleString())} outstanding.`,
        recommendation: "Run the invoicing batch before end of day.",
      });
    }

    if (!insights.length) {
      insights.push({
        id: "all-clear",
        tone: "success",
        title: "Operation running to plan",
        detail: "No SLA risks, capacity gaps or backlogs detected right now.",
      });
    }

    return insights;
  },
};
